// src/features/mech/sheetmetal/questions/PartWeightDimensionsQuestion.jsx
import React from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";

const WEIGHT_MIN = 0;
const DIMENSION_MIN = 0;

const weightError = (value) => {
  if (value === "") return "";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "Enter a valid number.";
  if (numericValue < WEIGHT_MIN) return `Min ${WEIGHT_MIN} g.`;
  return "";
};

const dimensionError = (value) => {
  if (value === "") return "";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "Enter a valid number.";
  if (numericValue < DIMENSION_MIN) return `Min ${DIMENSION_MIN} mm.`;
  return "";
};

const thicknessError = (value) => {
  if (value === "") return "";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "Enter a valid number.";
  if (numericValue < DIMENSION_MIN) return `Min ${DIMENSION_MIN} mm.`;
  return "";
};

export default function PartWeightDimensionsQuestion({ value, onChange, onNextStep }) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const dimensionValue = value || {
    finishWeight: "",
    partLength: "",
    partWidth: "",
    partHeight: "",
  };

  const weightHelperText = "Enter a positive weight in grams.";
  const dimensionHelperText = "Enter a positive value in mm.";
  const thicknessHelperText = "Enter a positive value in mm.";

  const handleFieldChange = (field, newValue) => {
    onChange?.({
      ...dimensionValue,
      [field]: newValue,
    });
  };

  // Handle Enter key on last field (Thickness) - proceed if all fields are filled
  const handleThicknessKeyDown = (e) => {
    if (e.key === "Enter" && onNextStep) {
      const allFieldsFilled =
        dimensionValue.finishWeight?.toString().trim() &&
        dimensionValue.partLength?.toString().trim() &&
        dimensionValue.partWidth?.toString().trim() &&
        dimensionValue.partHeight?.toString().trim();
      
      if (allFieldsFilled) {
        e.preventDefault();
        onNextStep();
      }
    }
  };

  return (
    
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
    <PerlLabelMedium sx={{ mb: 2, display: "block" }}>Enter Finish Weight, Length, Width, and Thickness:</PerlLabelMedium>
      <PerlTextField
        label="Finish weight (g)"
        required
        type="number"
        value={dimensionValue.finishWeight || ""}
        onChange={(e) => handleFieldChange("finishWeight", e.target.value)}
        inputProps={{ min: WEIGHT_MIN }}
        error={Boolean(weightError(dimensionValue.finishWeight))}
        helperText={weightError(dimensionValue.finishWeight) || weightHelperText}
        sx={{ mt: 1 }}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Length (mm)"
        required
        type="number"
        value={dimensionValue.partLength || ""}
        onChange={(e) => handleFieldChange("partLength", e.target.value)}
        inputProps={{ min: DIMENSION_MIN }}
        error={Boolean(dimensionError(dimensionValue.partLength))}
        helperText={dimensionError(dimensionValue.partLength) || dimensionHelperText}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Width (mm)"
        required
        type="number"
        value={dimensionValue.partWidth || ""}
        onChange={(e) => handleFieldChange("partWidth", e.target.value)}
        inputProps={{ min: DIMENSION_MIN }}
        error={Boolean(dimensionError(dimensionValue.partWidth))}
        helperText={dimensionError(dimensionValue.partWidth) || dimensionHelperText}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Thickness (mm)"
        required
        type="number"
        value={dimensionValue.partHeight || ""}
        onChange={(e) => handleFieldChange("partHeight", e.target.value)}
        onKeyDown={handleThicknessKeyDown}
        inputProps={{ min: DIMENSION_MIN }}
        error={Boolean(thicknessError(dimensionValue.partHeight))}
        helperText={thicknessError(dimensionValue.partHeight) || thicknessHelperText}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
    </Box>
  );
}

